import { z } from "zod";

const clipSchema = z.object({
  id: z.string(),
  url: z.string(),
  timeRange: z.object({
    startTimeMs: z.number(),
    endTimeMs: z.number(),
  }),
  inferenceJobId: z.string().nullish(),
});

export type MirageClip = z.infer<typeof clipSchema>;

export const mirageVideoFeedResponseSchema = z.object({
  videos: z.object({
    page: z.array(
      z.object({
        id: z.string(),
        createdAt: z.number(),
        completedAt: z.number().nullish(),
        initialVideoUrl: z.string(),
        caller: z.string(),
        userEntitlement: z.string().nullish(),
        isBookmarked: z.boolean().optional(),
      })
    ),
    pageInfo: z.object({
      hasNextPage: z.boolean(),
      endCursor: z.string().nullable(),
    }),
  }),
});

export const toggleBookmarkResponseSchema = z.object({
  bookmarked: z.boolean(),
});

export const dpoFlagRequestSchema = z.object({
  videoId: z.string().min(1),
  segmentId: z.string().min(1),
  inferenceJobId: z.string().min(1).optional(),
  reason: z.string().max(1000).optional(),
  flagId: z.string().uuid().optional(),
});

export const dpoFlagResponseSchema = z.object({
  segmentHash: z.string(),
});

export const bookmarkedVideoFeedResponseSchema = z.object({
  videos: z.object({
    page: z.array(
      z.object({
        id: z.string(),
        createdAt: z.number(),
        completedAt: z.number().nullish(),
        initialVideoUrl: z.string(),
        caller: z.string(),
        userEntitlement: z.string(),
        isBookmarked: z.literal(true),
      })
    ),
    pageInfo: z.object({
      hasNextPage: z.boolean(),
      endCursor: z.string().nullable(),
    }),
  }),
});

const llmMetadataSchema = z.object({
  model: z.string(),
  instructions: z.string(),
  output: z.string(),
});

const inferenceJobMetadataSchema = z.object({
  originalPrompt: z.string().nullish(),
  refinerSystemPrompt: z.string().nullish(),
  refinedPrompt: z.string().nullish(),
  mediaDescription: z.string().nullish(),
  audioDescription: llmMetadataSchema.nullish(),
  vlmDescription: llmMetadataSchema.nullish(),
  refinedVlmDescription: llmMetadataSchema.nullish(),
  appearanceRefine: llmMetadataSchema.nullish(),
  appearanceMerge: llmMetadataSchema.nullish(),
  behaviorMerge: llmMetadataSchema.nullish(),
  compute: z
    .object({
      provider: z.string(),
      machine: z.string(),
    })
    .nullish(),
});

const inferenceSegmentSchema = z.object({
  id: z.string(),
  url: z.string(),
  uri: z.string(),
  audio: z.object({
    url: z.string(),
    uri: z.string(),
  }),
  referenceImage: z.object({
    url: z.string().nullish(),
    uri: z.string().nullish(),
  }),
  referenceVideo: z
    .object({
      url: z.string().nullish(),
      uri: z.string().nullish(),
    })
    .optional(),
  generatedLatents: z.array(
    z.object({
      url: z.string().nullish(),
      uri: z.string().nullish(),
    })
  ),
  referencePrefix: z
    .object({
      url: z.string(),
      uri: z.string(),
      generatedLatents: z.array(
        z.object({
          url: z.string(),
          uri: z.string(),
        })
      ),
    })
    .nullish(),
  referenceSuffix: z
    .object({
      url: z.string(),
      uri: z.string(),
      generatedLatents: z.array(
        z.object({
          url: z.string(),
          uri: z.string(),
        })
      ),
    })
    .nullish(),
  inferenceConfig: z.object({
    url: z.string(),
  }),
  audioLatents: z
    .object({
      url: z.string(),
      uri: z.string(),
    })
    .nullish(),
  referenceImageLatents: z
    .object({
      url: z.string(),
      uri: z.string(),
    })
    .nullish(),
  metadata: z
    .object({
      seed: z.string().nullish(),
      vlmData: z
        .object({
          description: z.string(),
          instructions: z.string(),
        })
        .nullish(),
    })
    .nullish(),
  version: z.string().nullish(),
  evaluation: z.any().nullish(),
});

const inferenceJobSchema = z.object({
  id: z.string(),
  metadata: inferenceJobMetadataSchema,
  segments: z.array(inferenceSegmentSchema),
  evaluation: z.any().nullish(),
});

export type MirageInferenceJob = z.infer<typeof inferenceJobSchema>;

const audioSchema = z.object({
  assetId: z.string(),
  durationMs: z.number(),
  url: z.string(),
  wordTimestamps: z
    .array(
      z.object({
        text: z.string(),
        startTimeMs: z.number(),
        endTimeMs: z.number(),
      })
    )
    .nullish(),
  metadata: z
    .object({
      modelId: z.string().nullish(),
      modelName: z.string().nullish(),
      voiceId: z.string().nullish(),
      voiceName: z.string().nullish(),
    })
    .nullish(),
});

export type MirageVideoAudio = z.infer<typeof audioSchema>;

const actorTypeSchema = z.enum([
  "prompt",
  "stock",
  "generated_text_to_image",
  "generated_image_to_image",
  "generated_video_to_video",
  "uploaded",
  "twin",
  "captions_ingest",
]);

export const customActorTypeSchema = z.enum([
  "twin",
  "uploaded",
  "prompt",
  "generated_text_to_image",
  "generated_image_to_image",
  "generated_video_to_video",
]);

const mirageActorCopiedFromSchema = z
  .object({
    actorId: z.string(),
    userId: z.string().nullish(),
    copiedBy: z.string().nullish(),
    copiedAt: z.number().nullish(),
  })
  .nullish();

export const mirageActorsFeedResponseSchema = z.object({
  actors: z.object({
    page: z.array(
      z.object({
        id: z.string(),
        name: z.string().nullish(),
        type: actorTypeSchema,
        gender: z.enum(["male", "female"]).nullish(),
        visibility: z.enum(["private", "public"]),
        voiceId: z.string().nullish(),
        thumbnailUrl: z.string().nullish(),
        largeThumbnailUrl: z.string().nullish(),
        parentId: z.string().nullish(),
        variantIds: z.array(z.string()).nullish(),
        promptGenerationParams: z
          .object({
            characterPrompt: z.string().nullish(),
            actorPrompt: z.string().nullish(),
            backgroundPrompt: z.string().nullish(),
          })
          .nullish(),
        copiedFrom: mirageActorCopiedFromSchema,
        createdAt: z.number(),
        updatedAt: z.number().nullish(),
      })
    ),
    pageInfo: z.object({
      hasNextPage: z.boolean(),
      endCursor: z.string().nullable(),
    }),
  }),
});

export type MirageActorFeedItem = z.infer<
  typeof mirageActorsFeedResponseSchema
>["actors"]["page"][number];

const mirageActorAssetSchema = z.object({
  label: z.string(),
  assetId: z.string(),
  gcsUri: z.string().nullish(),
});

const mirageActorLookupItemSchema = z.object({
  id: z.string(),
  name: z.string().nullish(),
  type: actorTypeSchema,
  gender: z.enum(["male", "female"]).nullish(),
  visibility: z.enum(["private", "public"]),
  voiceId: z.string().nullish(),
  thumbnailUrl: z.string().nullish(),
  largeThumbnailUrl: z.string().nullish(),
  createdAt: z.number(),
  updatedAt: z.number().nullish(),
  copiedFrom: mirageActorCopiedFromSchema,
  assets: z.array(mirageActorAssetSchema),
});

export const mirageActorLookupResponseSchema = z.object({
  actor: mirageActorLookupItemSchema,
  variants: z.array(mirageActorLookupItemSchema),
});

export type MirageActorLookupItem = z.infer<typeof mirageActorLookupItemSchema>;

const appearanceSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("actor"),
    actorId: z.string(),
    url: z.string(),
    actorMetadata: z.object({
      gender: z.enum(["male", "female"]).nullish(),
      type: actorTypeSchema,
      promptGenerationParams: z
        .object({
          actorPrompt: z.string().nullish(),
          backgroundPrompt: z.string().nullish(),
        })
        .nullish(),
      imageGenerationParams: z
        .object({
          referenceImages: z
            .array(
              z.object({
                assetId: z.string(),
                url: z.string(),
              })
            )
            .nullish(),
        })
        .nullish(),
      voiceId: z.string().nullish(),
      referenceImageAssetId: z.string().nullish(),
      referenceImageUrl: z.string().nullish(),
    }),
  }),
  z.object({
    type: z.literal("prompt"),
    prompt: z.string(),
  }),
  z.object({
    type: z.literal("media"),
    mediaType: z.enum(["image", "video"]),
    assetId: z.string(),
    url: z.string(),
  }),
]);

export type MirageVideoAppearance = z.infer<typeof appearanceSchema>;

const geminiEvalSchema = z.object({
  video_id: z.string(),
  asset_id: z.string(),
  response: z
    .object({
      blurriness: z
        .object({
          score: z.number(),
          explanation: z.string(),
        })
        .optional(),
      camera_shake: z
        .object({
          score: z.number(),
          explanation: z.string(),
        })
        .optional(),
      ai_artifacts: z
        .object({
          score: z.number(),
          explanation: z.string(),
        })
        .optional(),
      bad_anatomy: z
        .object({
          score: z.number(),
          explanation: z.string(),
        })
        .optional(),
    })
    .catchall(
      z.object({
        score: z.number(),
        explanation: z.string(),
      })
    )
    .nullish(),
});

export const mirageVideoLookupResponseSchema = z.object({
  id: z.string(),
  exportUrl: z.string().nullable(),
  context: z.object({
    userId: z.string(),
    caller: z.string(),
    orgId: z.string().nullish(),
  }),
  clips: z.array(clipSchema),
  inferenceJobs: z.array(inferenceJobSchema).nullish(),
  input: z.object({
    audio: audioSchema,
    appearance: appearanceSchema,
    script: z.object({
      text: z.string(),
      language: z.string().nullish(),
    }),
  }),
  createdAt: z.number(),
  completedAt: z.number().nullish(),
  evals: z
    .object({
      gemini: geminiEvalSchema.nullable(),
    })
    .nullish(),
});

export const mirageAudioFeedResponseSchema = z.object({
  audio: z.object({
    page: z.array(
      z.object({
        id: z.string(),
        completedAt: z.number(),
        url: z.string(),
        referenceUrl: z.string().nullish(),
        text: z.string(),
        tags: z.array(z.string()).nullish(),
        ttsVoiceId: z.string().nullish(),
      })
    ),
    pageInfo: z.object({
      hasNextPage: z.boolean(),
      endCursor: z.string().nullable(),
    }),
  }),
});

const audioWordTimestampSchema = z.object({
  text: z.string(),
  startTime: z.number(),
  endTime: z.number(),
});

const audioSegmentSchema = z.object({
  targetText: z.string(),
  transcript: z.string(),
  wordTimestamps: z.array(audioWordTimestampSchema),
  startMs: z.number(),
  endMs: z.number(),
});

const inferenceResultSchema = z.object({
  url: z.string().nullish(),
  transcript: z.string().nullish(),
});

const reproducibilityInfoSchema = z.object({
  voiceDescription: z.string().nullish(),
  seed: z.string().nullish(),
  target: z.string().nullish(),
  referenceAudioUrl: z.string().nullish(),
  serializedComputationGraphState: z.string().nullish(),
});

const audioEvaluationResultSchema = z.object({
  explanation: z.string().nullish(),
  speechIntelligibility: z.number().nullish(),
  naturalness: z.number().nullish(),
  expressiveness: z.number().nullish(),
  technicalQuality: z.number().nullish(),
  pacing: z.number().nullish(),
  transcriptAlignment: z.number().nullish(),
  descriptionMatch: z.number().nullish(),
  referenceMatch: z.number().nullish(),
});

const inferenceRequestSchema = z.object({
  referenceAudioUri: z.string(),
  targetText: z.string(),
  voiceDescription: z.string().nullish(),
  seed: z.string().nullish(),
  modelVersion: z.string().nullish(),
});

export const voiceMetadataResponseSchema = z.object({
  name: z.string().nullish(),
  gender: z.string().nullish(),
  providerId: z.string().nullish(),
  accent: z.string().nullish(),
  previewUrl: z.string().nullish(),
});

export const mirageAudioLookupResponseSchema = z.object({
  id: z.string(),
  text: z.string(),
  seed: z.string().nullish(),
  segments: z.array(audioSegmentSchema),
  reference: z
    .object({
      transcript: z.string().nullish(),
      wordTimestamps: z.array(audioWordTimestampSchema).nullish(),
      url: z.string().nullish(),
    })
    .nullish(),
  audio: z.object({
    transcript: z.string(),
    url: z.string(),
    shadowUrl: z.string().optional(),
  }),
  createdAt: z.number(),
  completedAt: z.number(),
  inferenceResult: inferenceResultSchema.nullish(),
  reproducibilityInfo: reproducibilityInfoSchema.nullish(),
  audioEvalSummary: audioEvaluationResultSchema.nullish(),
  inferenceRequest: inferenceRequestSchema.nullish(),
  ttsVoiceId: z.string().nullish(),
});
